import * as React from 'react';

import classnames from "classnames";

import { Unit } from "../../../rest/Misc";
import { Select } from "../../../components/Select";
import { intToString, stringToInt } from "../../../converters/converters";
import { assertNumber, assertTrue, isNumber } from "../../utils/assert";
import { SettingsStoreState } from "./SettingsStore";
import { Polyglot } from '../../../polyglot';
import { FormItemHorizontal as FormItem} from '../Forms/FormItemHorizontal';
import { FormCheckboxItem } from '../Forms/FormCheckboxItem';
import { FormLink } from '../Forms/FormLink';
import { SettingsActions } from './SettingsActions';
import { LanguageOption } from '../../../polyglot/languagePacks';
import { NonIsolatedPremise } from "../NonIsolatedPremise";

const styles = require('./SettingsView.scss');

interface FormProps
{
    polyglot: Polyglot;
    isEmailNotificationEnabled: boolean;
    isAdmin: boolean;
}
type Props = SettingsStoreState & Partial<SettingsActions> & FormProps;

// SettingsView component renders the state from SettingsStore and emmits SettingsActions on user interactions
export class SettingsView extends React.Component<Props>
{
    state = {
        allowDeletionOfSensors: JSON.parse(localStorage.getItem("allowDeletionOfSensors")),
        dontNotifyLeavingRedZone: JSON.parse(localStorage.getItem("dontNotifyLeavingRedZone")),
        dontNotifyEnteringYellowZone: JSON.parse(localStorage.getItem("dontNotifyEnteringYellowZone")),
        dontNotifyLeavingYellowZone: JSON.parse(localStorage.getItem("dontNotifyLeavingYellowZone"))
    }
 
    allowDeletionOfSensors(val){
        localStorage.setItem("allowDeletionOfSensors", val)
        this.setState({
            allowDeletionOfSensors: val
        })
    }
    dontNotifyLeavingRedZone(val){
        localStorage.setItem("dontNotifyLeavingRedZone", val)
        this.setState({
            dontNotifyLeavingRedZone: val
        })
    }
    dontNotifyEnteringYellowZone(val){
        localStorage.setItem("dontNotifyEnteringYellowZone", val)
        this.setState({
            dontNotifyEnteringYellowZone: val
        })
    }
    dontNotifyLeavingYellowZone(val){
        localStorage.setItem("dontNotifyLeavingYellowZone", val)
        this.setState({
            dontNotifyLeavingYellowZone: val
        })
    }

    render()
    {
        const { polyglot, division, isAdmin } = this.props;
        if(!division)
            return null;

        return (
            <div className={classnames("form-horizontal", styles.SettingsView)}>
                <h3 className="text-center">{polyglot.t('settings.title_for', { name: division.Name })}</h3>
                <h4 className="text-center">{polyglot.t('settings.title_measure_units')}</h4>
                {
                    this.props.quantities.filter(qw => qw.IsUnitEditable).map(qw => {
                        const units = division.MustSpecifySettings ? qw.Units : [{ Id: null, Name: '---' }, ...qw.Units];
                        return (
                            <FormItem title={qw.Name} key={qw.Id} labelColumns={4} columns={4}>
                            {
                                (units.length > 1)
                                ?   <Select className='form-control'
                                        placeholder={division.MustSpecifySettings ? '---' : null}
                                        items={units}
                                        disabled={!isAdmin}
                                        defaultValue={intToString(this.props.quantitySettings[qw.Id], true)}
                                        valueFn={(u: Unit) => intToString(u.Id, true)}
                                        titleFn={(u: Unit) => u.Name}
                                        onChange={s => {
                                            const unitId = stringToInt(s.getValue(), true);
                                            assertTrue(unitId === null || isNumber(unitId), 'unitId has to be null or number');
                                            this.props.setQuantityUnit(qw.Id, unitId);
                                        }}
                                    />
                                :   <div className="form-control">
                                        <span>{qw.Name}</span>
                                    </div>
                            }
                            </FormItem>
                        )
                    })
                }
                { this.props.isEmailNotificationEnabled &&
                    <FormItem title={polyglot.t('settings.title_emails')} labelColumns={4}>
                        <textarea className = "form-control" rows={3} value={this.props.emails}
                            disabled={!isAdmin}
                            onChange={ta => this.props.setEmails(ta.target.value)}
                            onKeyDown={e => {
                                if(e.keyCode === 13) {
                                    e.stopPropagation(); // prevent form submition
                                }
                            }}
                            />
                    </FormItem>
                }

                <div className="form-group" style={{ marginBottom: '0px'}}>
                    <div className="col-sm-offset-4 col-sm-8">
                        <p style={{color: '#a31518', 'fontWeight': 'bold', margin: '0px'}}>The settings below only apply to vehicles within this division</p>
                    </div>
                </div>
                
                <FormCheckboxItem
                    disabled={!isAdmin}
                    checked={this.props.dontAlertLeavingRedZone} 
                    onChange={e => this.props.setDontAlertLeavingRedZone(e.target.checked)} 
                    label={"Do not notify on LEAVING RED zone (To YELLOW / GREEN)"} 
                />

                <FormCheckboxItem
                    disabled={!isAdmin}
                    checked={this.props.dontAlertEnteringYellowZone} 
                    onChange={e => this.props.setDontAlertEnteringYellowZone(e.target.checked)} 
                    label={"Do not notify on ENTERING YELLOW zone (From GREEN)"} 
                />

                <FormCheckboxItem
                    disabled={!isAdmin}
                    checked={this.props.dontAlertLeavingYellowZone} 
                    onChange={e => this.props.setDontAlertLeavingYellowZone(e.target.checked)} 
                    label={"Do not notify on LEAVING YELLOW zone (To GREEN)"} 
                />
                <hr/>
                <h3 className="text-center">{polyglot.t('settings.title_personal_settings')}</h3> 
                <NonIsolatedPremise>
                    <FormItem title={polyglot.t('settings.title_language')} labelColumns={4}>
                        <Select className='form-control'
                            placeholder="---"
                            items={this.props.languages}
                            titleFn={(lang: LanguageOption) => lang.display}
                            valueFn={(lang: LanguageOption) => lang.code}
                            onChange={s => this.props.setLanguage(this.props.languages.find(opt => opt.code === s.getValue()))}
                            defaultValue={this.props.selectedLanguage ? this.props.selectedLanguage.code : null}
                        />
                    </FormItem>
                </NonIsolatedPremise>

                {/*<FormCheckboxItem checked={this.props.passwordFlag} onChange={e => this.props.setChangePasswordFlag(e.target.checked)} label={polyglot.t('settings.title_change_password')} />*/}
 
                <div className="form-group" >
                    <div className="col-sm-offset-4 col-sm-12">
                        <a target="_blank" href='https://my.spartansense.com:8443/auth/realms/spartanlync/account/password'>
                            {polyglot.t('settings.title_change_password')}                            
                        </a>
                    </div>
                </div>
 
                {
                    <FormCheckboxItem
                        disabled={!isAdmin}
                        checked={this.state.allowDeletionOfSensors} 
                        onChange={e => this.allowDeletionOfSensors(e.target.checked)} 
                        label={"Allow Deletion of Sensors"} 
                    />
                }

                {/*<FormItem title={polyglot.t('person.label_password') + ':'}>
                    <input className='form-control' type="password"
                        disabled={!this.props.passwordFlag}
                        value={this.props.password}
                        onChange={e => this.props.setPassword(e.target.value)} />
                </FormItem>

                <FormItem title={polyglot.t('person.label_confirm_password') + ':'}>
                    <input className='form-control' type="password"
                        disabled={!this.props.passwordFlag}
                        value={this.props.passwordConfirmation}
                        onChange={e => this.props.setPasswordConfirmation(e.target.value)} />
                </FormItem>*/}

            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Settings/SettingsView.tsx